// Service area city data for Scotty's Land Specialist.
// Group A cities get all 7 services. Group B cities get weed control only.
// Each city has its own local line — used on that city's page only.

export type ServiceAreaGroup = "A" | "B";

export interface ServiceAreaCity {
  slug: string;
  name: string;
  group: ServiceAreaGroup;
  // BLOCK 1 — title
  title: string;
  // BLOCK 3 — h1
  h1: string;
  // BLOCK 4 — subhead: sentence one = Scott comes from Highland, sentence two = local line
  subhead: string;
  metaDescription: string;
  // the one local line for this city
  localLine: string;
  // nearby city slugs (3)
  nearby: string[];
}

// The seven service pages offered, in order, with display name + slug + description.
export const AREA_SERVICES = [
  {
    name: "Fast Snow Removal",
    slug: "snow-removal",
    description:
      "Driveways and walkways cleared during or after the storm. The city plows streets, not driveways — Scott clears yours.",
  },
  {
    name: "Fast Fall Cleanup",
    slug: "fall-cleanup",
    description:
      "Leaves, garden beds, and yard debris cleared off the lot before the snow packs them down. Open through December, and into January when the snow holds off.",
  },
  {
    name: "Reliable Dumpster Rental",
    slug: "dumpster-rental",
    description:
      "Container delivery and haul-off for remodel debris, garage cleanouts, and oak brush on large lots. The dumpster stays on your property for 24 hours.",
  },
  {
    name: "Reliable Land Clearing",
    slug: "land-clearing",
    description:
      "Brush, stumps, and roots taken out so the ground is actually usable — not just cut off at the trunk. Gambel oak resprouts from a lignotuber, so the roots have to go.",
  },
  {
    name: "Reliable Weed Control",
    slug: "weed-control",
    description:
      "Noxious weeds like field bindweed, goatheads, musk thistle, and Canada thistle treated at the root system so they do not come straight back.",
  },
  {
    name: "Reliable Spring Cleanup",
    slug: "spring-cleanup",
    description:
      "Winter debris, gravel pushed onto the grass, and the first weed flush cleared off the yard after the snow melts.",
  },
  {
    name: "Fast Junk Removal",
    slug: "junk-removal",
    description:
      "Furniture, brush, and remodel debris loaded and hauled off the property in one go — the piles a weekly can will not take.",
  },
];

// Weed control only — for Group B cities.
export const WEED_ONLY_SERVICES = [
  {
    name: "Reliable Weed Control",
    slug: "weed-control",
    description:
      "Noxious weeds like field bindweed, goatheads, musk thistle, and Canada thistle treated at the root system so they do not come straight back.",
  },
];

export function servicesForCity(city: ServiceAreaCity) {
  return city.group === "B" ? WEED_ONLY_SERVICES : AREA_SERVICES;
}

// Each Group A city has its own local line. Used on that city's page only.
const CITY_LOCAL_LINES: Record<string, string> = {
  highland: "Half-acre to acre lots. The city plows streets, not driveways.",
  alpine:
    "Large lots at the foot of American Fork Canyon, with more snow than the valley floor.",
  "cedar-hills": "Homes against the hills, on smaller lots than Highland.",
  "american-fork":
    "Dumpsters and cleanup in town. Clearing stays toward the canyon.",
  lehi: "Traverse Mountain sits on Highland's border. Dumpsters follow remodels.",
  suncrest:
    "Traverse Ridge above 6,000 feet, in Gambel oak. Snow is the heavy job.",
  "pleasant-grove": "Older homes with mature trees. Fall work is leaves.",
  bluffdale: "Horse properties. The drive crosses Point of the Mountain.",
  "saratoga-springs": "West of Utah Lake. Newer subdivisions.",
  draper: "South Draper is the close side. Snow gets heavier toward the ridge.",
  lindon: "Larger lots sit on the east side.",
  riverton: "South end of the Salt Lake Valley.",
  herriman: "Southwest side of the Salt Lake Valley.",
  "south-jordan": "North of Riverton, in the Salt Lake Valley.",
  sandy: "East bench of the Salt Lake Valley.",
  vineyard: "New homes on small lots, about 12 miles from Highland.",
  orem: "About 20 minutes south. Clearing stays on the east bench.",
  provo: "About 26 minutes south of Highland.",
  springville: "South of Provo.",
  "spanish-fork": "South of Springville.",
};

// Each Group B city has its own local line. Weed control only on these pages.
const WEED_LOCAL_LINES: Record<string, string> = {
  "west-mountain": "Open fields and rough parcels, 29 miles from Highland.",
  "lake-shore": "Ranches and open pasture, 20 miles from Highland.",
  "eagle-mountain": "Open desert ground, 14 miles from Highland.",
  "cedar-fort": "Farm town in Cedar Valley, 18 miles from Highland.",
  heber: "Wasatch Back ranches, 21 miles from Highland.",
  midway: "Pasture and ranches, 18 miles from Highland.",
  wallsburg: "Farm valley east of Highland, 20 miles from Highland.",
  "park-city": "Large mountain lots, 22 miles from Highland.",
  genola: "Cropland south of the lake, 30 miles from Highland.",
};

function buildCity(
  slug: string,
  name: string,
  nearby: string[],
): ServiceAreaCity {
  const localLine = CITY_LOCAL_LINES[slug];
  const label = `${name}, UT`;
  const title = `Reliable Land Clearing at Fair Prices in ${name}, Utah | Free Estimates`;
  const h1 = `Reliable Land Clearing at Fair Prices in ${name}, Utah`;
  const subhead =
    slug === "highland"
      ? `Scott is based right here in ${name}. ${localLine}`
      : `Scott comes from Highland to ${name}. ${localLine}`;
  const meta = `Reliable land clearing, weed control, dumpster rental, and seasonal cleanup in ${label}. Free estimates. Call or text 801-400-3346.`;
  return {
    slug,
    name,
    group: "A",
    title,
    h1,
    subhead,
    metaDescription: meta,
    localLine,
    nearby,
  };
}

function buildWeedCity(
  slug: string,
  name: string,
  nearby: string[],
): ServiceAreaCity {
  const localLine = WEED_LOCAL_LINES[slug];
  const label = `${name}, UT`;
  const title = `Reliable Weed Control in ${label} | Free Estimates`;
  const h1 = `Reliable Weed Control in ${name}, Utah`;
  const subhead = `Scott comes from Highland to ${name}. ${localLine}`;
  const meta = `Reliable weed control in ${label}. Scott Pymm controls noxious weeds on large lots and pastures. Free estimates. Call or text 801-400-3346.`;
  return {
    slug,
    name,
    group: "B",
    title,
    h1,
    subhead,
    metaDescription: meta,
    localLine,
    nearby,
  };
}

export const serviceAreaCities: ServiceAreaCity[] = [
  // GROUP A — all 7 services
  buildCity("highland", "Highland", ["alpine", "cedar-hills", "lehi"]),
  buildCity("alpine", "Alpine", ["highland", "cedar-hills", "american-fork"]),
  buildCity("cedar-hills", "Cedar Hills", [
    "highland",
    "alpine",
    "pleasant-grove",
  ]),
  buildCity("american-fork", "American Fork", [
    "highland",
    "pleasant-grove",
    "cedar-hills",
  ]),
  buildCity("lehi", "Lehi", ["highland", "alpine", "american-fork"]),
  buildCity("suncrest", "Suncrest", ["alpine", "highland", "lehi"]),
  buildCity("pleasant-grove", "Pleasant Grove", [
    "cedar-hills",
    "lindon",
    "american-fork",
  ]),
  buildCity("bluffdale", "Bluffdale", ["lehi", "alpine", "highland"]),
  buildCity("saratoga-springs", "Saratoga Springs", [
    "lehi",
    "bluffdale",
    "american-fork",
  ]),
  buildCity("draper", "Draper", ["suncrest", "alpine", "lehi"]),
  buildCity("lindon", "Lindon", [
    "pleasant-grove",
    "american-fork",
    "cedar-hills",
  ]),
  buildCity("riverton", "Riverton", ["bluffdale", "herriman", "draper"]),
  buildCity("herriman", "Herriman", ["riverton", "bluffdale", "south-jordan"]),
  buildCity("south-jordan", "South Jordan", ["herriman", "riverton", "sandy"]),
  buildCity("sandy", "Sandy", ["draper", "south-jordan", "lehi"]),
  buildCity("vineyard", "Vineyard", ["orem", "pleasant-grove", "lindon"]),
  buildCity("orem", "Orem", ["vineyard", "provo", "pleasant-grove"]),
  buildCity("provo", "Provo", ["orem", "springville", "vineyard"]),
  buildCity("springville", "Springville", ["provo", "spanish-fork", "orem"]),
  buildCity("spanish-fork", "Spanish Fork", ["springville", "provo", "orem"]),

  // GROUP B — weed control only
  buildWeedCity("west-mountain", "West Mountain", [
    "lake-shore",
    "spanish-fork",
    "genola",
  ]),
  buildWeedCity("lake-shore", "Lake Shore", [
    "spanish-fork",
    "west-mountain",
    "springville",
  ]),
  buildWeedCity("eagle-mountain", "Eagle Mountain", [
    "saratoga-springs",
    "lehi",
    "cedar-fort",
  ]),
  buildWeedCity("cedar-fort", "Cedar Fort", [
    "eagle-mountain",
    "saratoga-springs",
    "lehi",
  ]),
  buildWeedCity("heber", "Heber City", ["midway", "wallsburg", "park-city"]),
  buildWeedCity("midway", "Midway", ["heber", "wallsburg", "park-city"]),
  buildWeedCity("wallsburg", "Wallsburg", ["midway", "heber", "provo"]),
  buildWeedCity("park-city", "Park City", ["wallsburg", "midway", "heber"]),
  buildWeedCity("genola", "Genola", [
    "spanish-fork",
    "west-mountain",
    "lake-shore",
  ]),
];

export const getServiceAreaCity = (slug: string) =>
  serviceAreaCities.find((c) => c.slug === slug);

export const groupACities = serviceAreaCities.filter((c) => c.group === "A");
export const groupBCities = serviceAreaCities.filter((c) => c.group === "B");

export const allServiceAreaSlugs = serviceAreaCities.map((c) => c.slug);
